import { Capacitor } from '@capacitor/core';

/**
 * Informações de versão que podem ser usadas pela UI e pelos diagnósticos.
 *
 * As três superfícies da Athena evoluem em ritmos diferentes:
 * - appVersion: versão de produto exibida para a pessoa usuária;
 * - nativeVersion: binário Android/Capacitor instalado;
 * - webBundleVersion: bundle React entregue dentro do app ou na web;
 * - apiVersion: contrato público esperado com o Worker.
 *
 * Nenhuma destas variáveis habilita atualização automática. Em especial,
 * `webBundleVersion` é apenas identificador para suporte/diagnóstico; um
 * bundle remoto nunca é baixado ou executado por esta camada.
 */

const PRODUCT_VERSION_FALLBACK = '5.2.0';
const API_VERSION_FALLBACK = 'v1';
const VALID_UPDATE_CHANNELS = new Set(['development', 'preview', 'beta', 'stable']);

function environmentValue(name) {
  if (typeof process === 'undefined' || !process.env) return '';
  const value = process.env[name];
  return typeof value === 'string' ? value.trim() : '';
}

function versionValue(value, fallback) {
  // Versões são identificadores, não texto livre da conversa. Limitamos o
  // formato para que não acabem transportando dados acidentais aos diagnósticos.
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim();
  return /^[0-9A-Za-z][0-9A-Za-z._+-]{0,79}$/.test(normalized) ? normalized : fallback;
}

function resolveUpdateChannel() {
  const configured = environmentValue('REACT_APP_UPDATE_CHANNEL').toLowerCase();
  if (VALID_UPDATE_CHANNELS.has(configured)) return configured;
  return environmentValue('NODE_ENV') === 'production' ? 'stable' : 'development';
}

function runtimePlatform() {
  try {
    return Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
  } catch {
    return 'web';
  }
}

const platform = runtimePlatform();
const native = platform !== 'web';
const appVersion = versionValue(
  environmentValue('REACT_APP_ATHENA_APP_VERSION') || environmentValue('REACT_APP_VERSION'),
  PRODUCT_VERSION_FALLBACK,
);

/**
 * Snapshot síncrono e imutável, seguro para ser anexado a eventos locais.
 * O Gradle pode injetar REACT_APP_NATIVE_VERSION no build quando a versão do
 * binário divergir da versão de produto. Enquanto isso não existir, um app
 * nativo usa a mesma versão de produto como identificação conservadora.
 */
export const ATHENA_VERSION_INFO = Object.freeze({
  appVersion,
  nativeVersion: native
    ? versionValue(environmentValue('REACT_APP_NATIVE_VERSION'), appVersion)
    : null,
  webBundleVersion: versionValue(
    environmentValue('REACT_APP_WEB_BUNDLE_VERSION') || environmentValue('REACT_APP_BUILD_ID'),
    environmentValue('NODE_ENV') === 'production' ? 'unversioned' : 'development',
  ),
  apiVersion: versionValue(environmentValue('REACT_APP_API_VERSION'), API_VERSION_FALLBACK),
  updateChannel: resolveUpdateChannel(),
  platform,
  native,
});

/**
 * Retorna uma cópia para consumidores não conseguirem mutar o snapshot global.
 * A versão é de build/runtime; não contém conta, token, URL privada ou outro
 * dado sensível.
 */
export function getAthenaVersionInfo() {
  return { ...ATHENA_VERSION_INFO };
}

export const ATHENA_UPDATE_CHANNELS = Object.freeze([...VALID_UPDATE_CHANNELS]);
